import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Scale, Users, Award, Building2, UserX, CheckCircle2, HelpCircle, AlertCircle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Principle data shared across components
const PRINCIPLES = [
  { id: 'A', title: "Customers' Interest", desc: "Always act in the client's best interest", icon: Users, gradient: "from-blue-500 to-cyan-500", color: "blue" },
  { id: 'B', title: "Consistency", desc: "Same rules across all channels & products", icon: Scale, gradient: "from-indigo-500 to-blue-500", color: "indigo" },
  { id: 'C', title: "Accountability", desc: "One representative, one principal only", icon: Shield, gradient: "from-violet-500 to-indigo-500", color: "violet" },
  { id: 'D', title: "Independence", desc: "No commission, no product bias", icon: Award, gradient: "from-purple-500 to-violet-500", color: "purple" },
] as const;

// Quiz questions
const QUIZ_QUESTIONS = [
  {
    id: 1,
    question: "Which principle ensures that a financial adviser cannot represent more than one principal?",
    options: [
      { label: 'A', text: "Customers' Interest" },
      { label: 'B', text: "Consistency" },
      { label: 'C', text: "Accountability" },
      { label: 'D', text: "Independence" },
    ],
    correctLabel: 'C',
  },
  {
    id: 2,
    question: "Which of the following is considered an exempt person under the FAA?",
    options: [
      { label: 'A', text: "A freelance financial blogger" },
      { label: 'B', text: "A licensed bank providing investment advice" },
      { label: 'C', text: "A tied agent representing two insurance firms" },
      { label: 'D', text: "A newspaper columnist (commission-based)" },
    ],
    correctLabel: 'B',
  },
];

interface UOBVisualsProps {
  currentStage: number;
  quizQuestion?: number;
  activePrinciple?: string | null;
  exemptMode?: 'exempt-only' | 'comparison' | null;
  quizState?: 'waiting' | 'answered' | 'revealed';
  userQuizAnswer?: string | null;
  onQuizAnswer?: (answer: string) => void;
}

export function UOBVisuals({
  currentStage,
  quizQuestion = 1,
  activePrinciple = null,
  exemptMode = null,
  quizState = 'waiting',
  userQuizAnswer = null,
  onQuizAnswer,
}: UOBVisualsProps) {
  return (
    // Use flexbox to push content to bottom - more reliable than absolute positioning
    <div
      className="pointer-events-none z-10 flex flex-col justify-end"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        padding: '16px'
      }}
    >
      <AnimatePresence mode="wait">
        {/* Stage 1: Core Principles - Bottom caption style */}
        {currentStage === 1 && (
          <PrincipleCaption key="principles" activePrinciple={activePrinciple} />
        )}

        {/* Stage 2: Exempt & Excluded */}
        {currentStage === 2 && exemptMode === 'exempt-only' && (
          <ExemptOnlyOverlay key="exempt-only" />
        )}
        {currentStage === 2 && exemptMode === 'comparison' && (
          <ExemptComparisonOverlay key="exempt-comparison" />
        )}

        {/* Stage 3: Quiz - only show when quizQuestion > 0 */}
        {currentStage === 3 && quizQuestion > 0 && (
          <QuizOverlay
            key="quiz"
            questionNumber={quizQuestion}
            quizState={quizState}
            userAnswer={userQuizAnswer}
            onAnswer={onQuizAnswer}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Bottom caption showing current principle being discussed
function PrincipleCaption({ activePrinciple }: { activePrinciple: string | null }) {
  const principle = activePrinciple && activePrinciple !== 'ALL'
    ? PRINCIPLES.find(p => p.id === activePrinciple)
    : null;
  const showAll = activePrinciple === 'ALL';

  return (
    <div className="w-full flex justify-center">
      <AnimatePresence mode="wait">
        {showAll ? (
          // All 4 principles displayed together
          <motion.div
            key="all-four"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden max-w-3xl w-full"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-5 py-3 flex items-center justify-between">
              <h3 className="font-bold text-white text-sm">Four Core Principles of FAA</h3>
              <div className="flex items-center gap-1.5 bg-blue-500/20 rounded-full px-3 py-1">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-xs font-medium text-blue-200">Overview</span>
              </div>
            </div>

            {/* 4 Principles Grid */}
            <div className="p-4 grid grid-cols-4 gap-3">
              {PRINCIPLES.map((p, idx) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08, type: "spring", damping: 20 }}
                  className="bg-slate-50 rounded-xl p-3 border border-slate-100"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={cn("p-1.5 rounded-lg bg-gradient-to-br text-white", p.gradient)}>
                      <p.icon className="w-3.5 h-3.5" />
                    </div>
                    <span className={cn("w-5 h-5 rounded flex items-center justify-center text-xs font-bold bg-gradient-to-br text-white", p.gradient)}>
                      {p.id}
                    </span>
                  </div>
                  <h4 className="font-semibold text-slate-900 text-xs leading-tight">{p.title}</h4>
                  <p className="text-[10px] text-slate-500 mt-1 leading-tight">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : principle ? (
          // Single principle caption
          <motion.div
            key={principle.id}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden max-w-lg w-full"
          >
            {/* Gradient accent */}
            <div className={cn("h-1 bg-gradient-to-r", principle.gradient)} />

            <div className="p-4 flex items-center gap-4">
              {/* Icon */}
              <div className={cn("p-3 rounded-xl bg-gradient-to-br text-white shadow-lg", principle.gradient)}>
                <principle.icon className="w-5 h-5" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={cn("w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold bg-gradient-to-br text-white", principle.gradient)}>
                    {principle.id}
                  </span>
                  <h3 className="font-bold text-slate-900">{principle.title}</h3>
                </div>
                <p className="text-sm text-slate-600">{principle.desc}</p>
              </div>

              {/* Currently discussing badge */}
              <div className="flex items-center gap-1.5 bg-blue-50 rounded-full px-3 py-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                <span className="text-xs font-medium text-blue-700">Discussing</span>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

// Exempt Persons only (single card) - positioned at bottom right
function ExemptOnlyOverlay() {
  return (
    <div className="w-full flex justify-end">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border-l-4 border-emerald-500"
      >
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-5 border-b border-emerald-100 flex items-center gap-3">
          <div className="p-2.5 bg-emerald-500 rounded-xl shadow-lg">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-emerald-900 text-lg">Exempt Persons</h3>
            <p className="text-emerald-700 text-sm">Under the FAA</p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-slate-600 font-medium">Already regulated under other Acts:</p>
          <ul className="space-y-3">
            {[
              'Licensed banks & insurance companies',
              'Capital markets services licence holders',
              'Co-operative societies & finance companies',
              'Approved exchanges & market operators'
            ].map((item, i) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (i * 0.08) }}
                className="flex items-start gap-3 text-slate-700"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="pt-4 border-t border-slate-100 flex items-center gap-2 text-sm text-slate-500"
          >
            <AlertCircle className="w-4 h-4" />
            <span>Still subject to FAA conduct rules</span>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

// Exempt vs Excluded comparison - compact side-by-side design
// Optimized to fit within video overlay without being cut off
function ExemptComparisonOverlay() {
  return (
    <div className="w-full flex justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden max-w-3xl w-full"
      >
        {/* Unified Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-5 py-3 flex items-center justify-between">
          <h3 className="font-bold text-white text-sm">Exempt vs Excluded Persons</h3>
          <div className="flex items-center gap-1.5 bg-blue-500/20 rounded-full px-3 py-1">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-xs font-medium text-blue-200">Comparison</span>
          </div>
        </div>

        {/* Two-column comparison grid */}
        <div className="grid grid-cols-2 divide-x divide-slate-200">
          {/* Exempt Persons Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, type: "spring", damping: 20 }}
            className="p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-emerald-500 rounded-lg">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-bold text-emerald-900 text-sm">Exempt Persons</h4>
            </div>

            <p className="text-xs text-slate-500 mb-2 font-medium">Regulated under other Acts</p>

            <ul className="space-y-1.5 mb-3">
              {['Licensed Banks', 'Insurance Companies', 'Capital Markets Holders'].map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 + (i * 0.05) }}
                  className="flex items-center gap-2 text-slate-700 text-xs"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>

            <div className="bg-emerald-50 rounded-lg px-2.5 py-1.5">
              <p className="text-[11px] text-emerald-700 flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>Still subject to FAA conduct rules</span>
              </p>
            </div>
          </motion.div>

          {/* Excluded Persons Column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15, type: "spring", damping: 20 }}
            className="p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-amber-500 rounded-lg">
                <UserX className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-bold text-amber-900 text-sm">Excluded Persons</h4>
            </div>

            <p className="text-xs text-slate-500 mb-2 font-medium">Advice incidental to main job</p>

            <ul className="space-y-1.5 mb-3">
              {['Lawyers', 'Accountants', 'Newspapers (no commission)'].map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.25 + (i * 0.05) }}
                  className="flex items-center gap-2 text-slate-700 text-xs"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>

            <div className="bg-amber-50 rounded-lg px-2.5 py-1.5">
              <p className="text-[11px] text-amber-700 flex items-center gap-1.5">
                <AlertCircle className="w-3 h-3 flex-shrink-0" />
                <span>Not regulated under FAA</span>
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

// Interactive Quiz - positioned at bottom right
// Compact design to fit within video overlay without being cut off
function QuizOverlay({
  questionNumber,
  quizState,
  userAnswer,
  onAnswer,
}: {
  questionNumber: number;
  quizState: 'waiting' | 'answered' | 'revealed';
  userAnswer: string | null;
  onAnswer?: (answer: string) => void;
}) {
  const totalQuestions = QUIZ_QUESTIONS.length;
  const currentQ = QUIZ_QUESTIONS[questionNumber - 1];
  const isInteractive = quizState === 'waiting' && onAnswer;

  const getOptionState = (label: string) => {
    const isCorrect = label === currentQ.correctLabel;
    const isSelected = label === userAnswer;

    // Safety check: Only show revealed state if user has actually answered
    // This prevents showing correct answer on question transition before user responds
    if (quizState === 'revealed') {
      // If no user answer yet, don't reveal the correct answer
      // This handles race conditions during question transitions
      if (!userAnswer) {
        return 'neutral';
      }
      if (isCorrect) return 'correct';
      if (isSelected && !isCorrect) return 'incorrect';
      return 'neutral';
    }

    if (quizState === 'answered' && isSelected) return 'selected';

    return 'neutral';
  };

  return (
    <div className="w-full flex justify-end">
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -30, scale: 0.95 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
              <HelpCircle className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-sm font-bold text-white">Knowledge Check</h2>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {Array.from({ length: totalQuestions }).map((_, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    idx + 1 < questionNumber ? "bg-emerald-400" :
                    idx + 1 === questionNumber ? "bg-white scale-110" : "bg-white/30"
                  )}
                />
              ))}
            </div>
            <span className="text-white/70 text-xs font-medium">{questionNumber}/{totalQuestions}</span>
          </div>
        </div>

        {/* Question - more compact */}
        <div className="p-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQ.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {/* Question text with inline badge */}
              <p className="text-sm font-semibold text-slate-800 mb-3 leading-relaxed">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-bold mr-2">
                  {questionNumber}
                </span>
                {currentQ.question}
              </p>

              {/* Options - compact */}
              <div className={cn("space-y-1.5", isInteractive && "pointer-events-auto")}>
                {currentQ.options.map((opt, idx) => {
                  const state = getOptionState(opt.label);

                  return (
                    <motion.button
                      key={opt.label}
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + idx * 0.04 }}
                      onClick={() => isInteractive && onAnswer?.(opt.label)}
                      disabled={!isInteractive}
                      className={cn(
                        "w-full flex items-center gap-2.5 p-2.5 rounded-lg border-2 transition-all duration-200 text-left",
                        state === 'correct' && "bg-emerald-50 border-emerald-400 shadow-md",
                        state === 'incorrect' && "bg-red-50 border-red-400",
                        state === 'selected' && "bg-blue-50 border-blue-400 shadow-md",
                        state === 'neutral' && "bg-slate-50/80 border-slate-100",
                        isInteractive && "hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer active:scale-[0.98]"
                      )}
                    >
                      {/* Label */}
                      <span className={cn(
                        "w-7 h-7 rounded-md flex items-center justify-center font-bold text-xs transition-all shadow-sm flex-shrink-0",
                        state === 'correct' && "bg-emerald-500 text-white",
                        state === 'incorrect' && "bg-red-500 text-white",
                        state === 'selected' && "bg-blue-500 text-white",
                        state === 'neutral' && "bg-white text-slate-400 border border-slate-200"
                      )}>
                        {opt.label}
                      </span>

                      {/* Text */}
                      <span className={cn(
                        "flex-1 text-sm font-medium transition-colors",
                        state === 'correct' && "text-emerald-900",
                        state === 'incorrect' && "text-red-900",
                        state === 'selected' && "text-blue-900",
                        state === 'neutral' && "text-slate-600"
                      )}>
                        {opt.text}
                      </span>

                      {/* Status icon */}
                      {state === 'correct' && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", damping: 10 }}
                          className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"
                        >
                          <CheckCircle2 className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                      {state === 'incorrect' && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", damping: 10 }}
                          className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0"
                        >
                          <XCircle className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                    </motion.button>
                  );
                })}
              </div>

              {/* Instruction hint - compact */}
              {quizState === 'waiting' && !userAnswer && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-3 text-center text-xs text-slate-500"
                >
                  Select an option, then say your answer
                </motion.p>
              )}
              {/* After selection, prompt to speak */}
              {quizState === 'waiting' && userAnswer && (
                <motion.p
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 text-center text-xs text-blue-600 font-medium"
                >
                  Now say "{userAnswer}" to confirm your answer
                </motion.p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
